import React, { useRef, useMemo, useState } from 'react';
import type { FC, CSSProperties } from 'react';
import { useNamedContainers } from '@alfa-bank/site-named-container/utils';
import cn from 'classnames';
import debounce from 'lodash/debounce';

import { useTouch } from '../../hooks/use-touch';
import { useIsTouchDevice } from '../../hooks/use-is-touch-device';

import type { TChildHandler } from './components/top-menu/models';
import type { TMainHeader } from './models';
import { TOP_MENU_CONFIG } from './helpers';

export const MainHeader: FC<TMainHeader> = ({
    AlfaIcon,
    LayoutDesktop,
    Grid,
    TopMenu,
    Stack,
    YandexSearchBar,
    backgroundType = TOP_MENU_CONFIG.BACKGROUND,
    children,
    logo = '/',
    menuGroups,
    onMenuClose,
    onMenuOpen,
    onSearchIconClick,
    stackingOrder,
    styles,
    theme = 'light',
    yaSearchPlaceholder = TOP_MENU_CONFIG.SEARCH_PLACEHOLDER,
    yaSearchURL,
    suggestList,
    suggestTitle,
    zeroHeight,
}) => {
    const { buttonSlot, logoSlot, ...rest } = useNamedContainers(children);
    const [activeTheme, setActiveTheme] = useState(theme);
    const [showSuggests, setShowSuggests] = useState(false);
    const themeStyle = theme === 'dark' ? styles.darkTheme : '';
    const isTouchDevice = useIsTouchDevice();

    const mainRef = useRef<HTMLDivElement>(null);
    const tabGroupRef = useRef<HTMLDivElement & TChildHandler>(null);
    const dropDownRef = useMemo<Record<string, HTMLDivElement | null>>(() => ({}), []);

    const showContainer = () => {
        if (theme === 'light') setActiveTheme('dark');
        mainRef?.current?.classList.add(styles.isOpen);
        onMenuOpen?.();
    };

    const { activeTab, handleTouch } = useTouch();

    const hideContainer = debounce(
        () => {
            if (theme !== activeTheme) setActiveTheme('light');
            mainRef?.current?.classList.remove(styles.isOpen);
            tabGroupRef?.current?.switchoffActiveTag();
            handleTouch('');
            onMenuClose?.();
        },
        /* istanbul ignore next */
        isTouchDevice ? 401 : 300
    );

    return (
        <>
            <Stack value={stackingOrder.OVERLAY}>
                {zIndex => (
                    <div
                        ref={mainRef}
                        className={cn(styles.main, themeStyle, { [styles.zeroHeight]: zeroHeight })}
                        data-test-id="test-main"
                        style={{ zIndex }}
                    >
                        <div className={styles.wrapper}>
                            <div
                                className={styles.contentBlock}
                                data-test-id="test-content-block"
                                onBlur={hideContainer}
                                onMouseEnter={() => {
                                    hideContainer.cancel();
                                }}
                                onMouseLeave={hideContainer}
                                // Хак для safari, нужно для того чтобы сработал onBlur при взаимодействии с другими элементами
                                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                                tabIndex={0}
                            >
                                <div
                                    className={styles.dropDownContainer}
                                    style={{ '--top-lvl-zindex': zIndex } as CSSProperties}
                                >
                                    {Object.entries(rest).map(([key, child]) => (
                                        <div
                                            key={key}
                                            ref={ref => {
                                                dropDownRef[key] = ref;
                                            }}
                                            className={cn(styles.dropDown)}
                                            data-test-id="dd-content"
                                        >
                                            {child}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div
                            onBlur={hideContainer}
                            onMouseEnter={() => hideContainer.cancel()}
                            onMouseLeave={hideContainer}
                            // Хак для safari, нужно для того чтобы сработал onBlur при взаимодействии с другими элементами
                            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                            tabIndex={0}
                        >
                            <LayoutDesktop>
                                <Grid
                                    className={cn(styles.external, styles.topMenuContainer)}
                                    classNames={{
                                        col: cn(
                                            styles.external,
                                            styles.external,
                                            styles.gridColumns,
                                            { [styles.hiddenMenu]: showSuggests }
                                        ),
                                    }}
                                    colAutoWidth
                                    collapse
                                    columnsMargin={0}
                                    flexDirection="row"
                                    justify="start"
                                    styler={{ paddingTop: 32 }}
                                >
                                    {logoSlot ?? <AlfaIcon href={logo} />}
                                    <TopMenu
                                        ref={tabGroupRef}
                                        activeTab={activeTab}
                                        dropDownMapRef={dropDownRef}
                                        menuGroup={menuGroups}
                                        onTabHover={showContainer}
                                        onTabTouch={handleTouch}
                                        theme={activeTheme}
                                    />
                                    <YandexSearchBar
                                        onSearchIconClick={onSearchIconClick}
                                        placeholder={yaSearchPlaceholder}
                                        searchId={TOP_MENU_CONFIG.SEARCH_ID}
                                        searchUrl={yaSearchURL}
                                        setShowSuggests={setShowSuggests}
                                        showSuggests={showSuggests}
                                        suggestList={suggestList}
                                        suggestTitle={suggestTitle}
                                    />
                                    <div className={styles.rightMenuContainer}>
                                        <div className={styles.buttonSlotContainer}>
                                            {buttonSlot}
                                        </div>
                                    </div>
                                </Grid>
                            </LayoutDesktop>
                        </div>
                    </div>
                )}
            </Stack>
            <Stack value={stackingOrder.POSITIONER}>
                {zIndex => (
                    <div
                        className={cn(styles.curtains, styles[backgroundType])}
                        style={{ zIndex }}
                    />
                )}
            </Stack>
        </>
    );
};
